// components/Cart.js
import React, { useState, useEffect } from 'react';
import useCartStore from '../store/cartStore';
import useUserStore from '../store/userStore';
import { prepareCheckoutEvent } from '../utils/analytics';
import { handleCheckout } from '../utils/singleItemCheckout'; // Импортируем функцию handleCheckout

function Cart() {
  const cartItems = useCartStore((state) => state.cartItems);
  const totalPrice = useCartStore((state) => state.totalPrice);
  const removeFromCart = useCartStore((state) => state.removeFromCart);
  const clearCart = useCartStore((state) => state.clearCart);
  const closeCart = useCartStore((state) => state.closeCart);
  const [fadingOut, setFadingOut] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const { isLoggedIn, fetchWithTokenRefresh, refreshTokenIfNeeded } = useUserStore();
  const [removePending, setRemovePending] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  // Effect for cart animation on mount
  useEffect(() => {
    // Immediately set visible without delay
    setIsVisible(true);
    
    return () => {
      setIsVisible(false);
    };
  }, []);

  // Ensure we clear any stuck removal state when cart changes
  useEffect(() => {
    if (removePending && cartItems.length > 0) {
      setTimeout(() => setRemovePending(false), 1000);
    }
  }, [cartItems, removePending]);

  const handleRemove = async (assetId) => {
    // Prevent duplicate remove operations
    if (removePending || fadingOut.includes(assetId)) {
      return;
    }
    
    setRemovePending(true);
    
    // Start fading animation
    setFadingOut([...fadingOut, assetId]);
    
    try {
      // Call removeFromCart after a brief animation delay
      setTimeout(async () => {
        const success = await removeFromCart(assetId);
        
        if (!success) {
          console.error("[CART] Failed to remove item from cart:", assetId);
        }
        
        // Always clear the item from fading state
        setFadingOut((prev) => prev.filter((id) => id !== assetId));
        setRemovePending(false);
      }, 500);
    } catch (error) {
      console.error("[CART] Error in handleRemove:", error);
      setFadingOut((prev) => prev.filter((id) => id !== assetId));
      setRemovePending(false);
    }
  };

  const handleClearCart = (e) => {
    e.preventDefault();
    clearCart();
    closeCart();
  };

  // Обновленная функция handleCheckout, использующая утилиту singleItemCheckout
  const handleCheckoutCart = async () => {
    console.log("[CART] Starting checkout process for cart", { itemCount: cartItems.length });
    
    // Используем общую функцию handleCheckout из утилиты singleItemCheckout
    const result = await handleCheckout({
      items: cartItems,
      totalPrice: totalPrice,
      setLoading: setCheckoutLoading,
      setError: setCheckoutError,
      returnUrl: window.location.href
    });
    
    // Обрабатываем результат, если нет редиректа
    if (!result.success && !result.redirected) {
      console.error("[CART] Checkout failed but no redirect happened:", result.error);
    }
  };

  return (
    <div 
      className={`max-w-7xl w-[90%] mx-auto overflow-hidden transition-all duration-500 ease-in-out ${
        isVisible 
          ? 'opacity-100 transform translate-y-0' 
          : 'opacity-0 transform translate-y-5'
      }`}
    >
      {cartItems.length === 0 ? (
        <div className="flex justify-center items-center p-8 text-center">
          <p className="text-lg">Your cart is empty.</p>
        </div>
      ) : (
        <>
          {/* Мобильная версия - показывается только на малых экранах */}
          <div className="grid grid-cols-6 gap-4 md:hidden">
            <div className="col-span-4 sm:col-span-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 overflow-y-auto max-h-[380px] pr-2.5 pb-5 w-full">
                {cartItems.map((item) => (
                  <div 
                    className={`relative rounded-lg border border-black/10 h-[210px] flex justify-center items-center mt-0.5 w-full ${
                      fadingOut.includes(item.assetId) ? 'opacity-0 transform -translate-y-5 transition-all duration-500' : ''
                    }`} 
                    key={item.assetId || item.id}
                  >
                    {item.mediaType === 'icon' && item.slug && item.collection && (
                      <div className="flex justify-center items-center h-full">
                        <img
                          src={`https://icons-pbl.davooda.com/${item.collection}/png-256px/${item.slug}-256x256.png`}
                          alt={item.alt || 'Icon'}
                          className="w-16 h-16"
                        />
                      </div>
                    )}
                    {item.mediaType === 'iconset' && item.slug && (
                      <div className="flex justify-center items-center">
                        <img
                          src={`https://icons-pbl.davooda.com/outline/set-preview/${item.slug}.png`}
                          alt={item.alt || 'Icon Set'}
                          className="w-[164px] h-auto mt-5"
                        />
                      </div>
                    )}
                    <div className="absolute top-1.5 right-2.5 text-xs">
                      ${item.product && typeof item.product.price === 'number' ? item.product.price.toFixed(2) : '0.00'}{' '}
                      <button
                        className="text-black underline ml-1 cursor-pointer"
                        disabled={fadingOut.includes(item.assetId) || removePending}
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemove(item.assetId);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-2 pl-0 md:pl-2.5 flex flex-col items-start">
              <div className="text-2xl text-black mb-5">
                Total: ${typeof totalPrice === 'number' ? totalPrice.toFixed(2) : '0.00'}
              </div>
              
              {checkoutError && (
                <div className="w-full bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  <p>{checkoutError}</p>
                </div>
              )}
              
              <button
                className="w-full bg-black text-white font-bold py-3.5 px-2.5 rounded transition-all duration-300 text-base mt-4"
                title="Proceed to checkout"
                onClick={handleCheckoutCart}
                disabled={checkoutLoading}
              >
                Proceed to checkout
                {checkoutLoading && (
                  <span className="ml-4 inline-block align-middle">
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 100 100" 
                      preserveAspectRatio="xMidYMid" 
                      className="align-middle"
                    >
                      <circle cx="50" cy="50" fill="none" stroke="#fff" strokeWidth="8" r="35" strokeDasharray="164.93361431346415 56.97787143782138">
                        <animateTransform 
                          attributeName="transform" 
                          type="rotate" 
                          repeatCount="indefinite" 
                          dur="1s" 
                          values="0 50 50;360 50 50" 
                          keyTimes="0;1"
                        />
                      </circle>
                    </svg>
                  </span>
                )}
              </button>
              <div className="mt-10">
                <button onClick={handleClearCart} className="text-black underline">
                  Clear Cart
                </button>
              </div>
            </div>
          </div>

          {/* Десктопная версия - скрыта на малых экранах, сетка 8 колонок */}
          <div className="hidden md:flex md:flex-row md:gap-4">
            <div className="w-3/4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 overflow-y-auto max-h-[380px] pr-2.5 pb-5 w-full">
                {cartItems.map((item) => (
                  <div 
                    className={`relative rounded-lg border border-black/10 h-[210px] flex justify-center items-center mt-0.5 w-full ${
                      fadingOut.includes(item.assetId) ? 'opacity-0 transform -translate-y-5 transition-all duration-500' : ''
                    }`} 
                    key={item.assetId || item.id}
                  >
                    {item.mediaType === 'icon' && item.slug && item.collection && (
                      <div className="flex justify-center items-center h-full">
                        <img
                          src={`https://icons-pbl.davooda.com/${item.collection}/png-256px/${item.slug}-256x256.png`}
                          alt={item.alt || 'Icon'}
                          className="w-16 h-16"
                        />
                      </div>
                    )}
                    {item.mediaType === 'iconset' && item.slug && (
                      <div className="flex justify-center items-center">
                        <img
                          src={`https://icons-pbl.davooda.com/outline/set-preview/${item.slug}.png`}
                          alt={item.alt || 'Icon Set'}
                          className="w-[164px] h-auto mt-5"
                        />
                      </div>
                    )}
                    <div className="absolute top-1.5 right-2.5 text-xs">
                      ${item.product && typeof item.product.price === 'number' ? item.product.price.toFixed(2) : '0.00'}{' '}
                      <button
                        className="text-black underline ml-1 cursor-pointer"
                        disabled={fadingOut.includes(item.assetId) || removePending}
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemove(item.assetId);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-1/4">
              <div className="text-2xl text-black mb-5">
                Total: ${typeof totalPrice === 'number' ? totalPrice.toFixed(2) : '0.00'}
              </div>
              
              {checkoutError && (
                <div className="w-full bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  <p>{checkoutError}</p>
                </div>
              )}
              
              <button
                className="w-full bg-black text-white font-bold py-3.5 px-2.5 rounded transition-all duration-300 text-base mt-4"
                title="Proceed to checkout"
                onClick={handleCheckoutCart}
                disabled={checkoutLoading}
              >
                Proceed to checkout
                {checkoutLoading && (
                  <span className="ml-4 inline-block align-middle">
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 100 100" 
                      preserveAspectRatio="xMidYMid" 
                      className="align-middle"
                    >
                      <circle cx="50" cy="50" fill="none" stroke="#fff" strokeWidth="8" r="35" strokeDasharray="164.93361431346415 56.97787143782138">
                        <animateTransform 
                          attributeName="transform" 
                          type="rotate" 
                          repeatCount="indefinite" 
                          dur="1s" 
                          values="0 50 50;360 50 50" 
                          keyTimes="0;1"
                        />
                      </circle>
                    </svg>
                  </span>
                )}
              </button>
              <div className="mt-10">
                <button onClick={handleClearCart} className="text-black underline">
                  Clear Cart
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Cart;